import type { GroupConditions } from "../../features/Taglist"
import type { FeatureData, SchemaFeature } from "../../features/Workflows"
import type { ConditionListFile } from "../generated/api/ConditionListFile"
import { RuleSetCreateRequest } from "../generated/api/RuleSetCreateRequest"
import type { TagListCreateRequest } from "../generated/api/TagListCreateRequest"
import { WorkflowInputFeature } from "../generated/api/WorkflowInputFeature"
import { Operators } from "./projects"

/**
 * all related custom types for ruleset component.
 */

export type CreateRulesetRequest = {
  projectId: string
  name: string
  rulesUUID?: string[]
  rules?: Array<CustomRuleRequest>
  type?: RuleSetCreateRequest.type
}

export type CreateTaglistRequest = {
  projectId: string
  name: string
  rules?: Array<TagListCreateRequest>
}

export type UpdateTaglistRequest = {
  projectId: string
  taglistId?: string
  name?: string
  rules?: Array<TagListCreateRequest>
}

export type CloneDataFilesRequest = {
  project_uuid: string
  data_files: { uuid: string; version: string }[]
}

export type UpdateRulesetRequest = {
  projectId: string
  rulesetId?: string
  name?: string
  rules?: Array<CustomRuleRequest>
  type?: RuleSetCreateRequest.type
}

export interface CustomRule {
  name: string
  readonly created_at: string
  uuid?: string
  return_label?: string
  condition: string
  feature_nesting_separator?: string
  readonly condition_list_files?: ConditionListFile | null
}

export type CustomTag = Omit<CustomRule, "return_label">

export interface CustomRuleRequest extends Omit<CustomRule, "created_at" | "condition_list_files"> {
  readonly condition_list_files?: ConditionListFile[]
  readonly created_at?: string
}

export type CustomTagRequest = Omit<CustomRuleRequest, "return_label">

export interface BaseCondition {
  id?: number | string
  feature: string
  operator: string
  type?: string
  value: string
  valueOrFeature?: "Value" | "Feature"
  secondValue?: string
  secondFeature?: string
}

export interface Rule extends BaseCondition {
  andOr: string
  schemaFeatureType: WorkflowInputFeature.type
}

export interface FilterCondition extends BaseCondition {
  andOr: "and" | "or"
}

export type ConditionFeatureChange = {
  filteredFeatures: Array<FeatureData | SchemaFeature>
  otherFilteredFeatures: Array<FeatureData | SchemaFeature>
  operator: Operators | string
  operators: Operators[] | string[]
  types: Array<string>
  type: string
  value: string
}

export enum MapValueTypeToFeatureType {
  "true" = WorkflowInputFeature.type.BOOLEAN,
  "false" = WorkflowInputFeature.type.BOOLEAN,
  "null" = WorkflowInputFeature.type.BOOLEAN,
  "string" = WorkflowInputFeature.type.TEXT,
  "number" = WorkflowInputFeature.type.NUMBER,
}

export type VersionChangeRequest = {
  projectId: string
  resourceId: string
  resource:
    | "taglists"
    | "rulesets"
    | "scorecardsets"
    | "scorecards"
    | "workflows"
    | "programs"
    | "scripts"
    | "data-files"
    | "calculators"
  version: string
}

export type SingleTagCard = {
  id: string
  tagName: string
  conditions: Rule[]
  groups: GroupConditions[]
  condition_list_files?: ConditionListFile | null
}

export type SingleRuleCard = {
  id: string
  name: string
  conditions: Rule[]
  groups: GroupConditions[]
  label: string
  condition_list_files: ConditionListFile | null
}

export interface RetrieveLabelsRequest {
  project_uuid: string
  search?: string | null
  page: number
  pageSize: number
}

export type NewFeatureData = {
  isRequired: boolean
  type: WorkflowInputFeature.type
  workflow: string
}

export type getNewRuleInfoResult = {
  commonFeature: { name: string; type: string | undefined }
  nonEmptyFeatures: Rule[]
}
